/* eslint-disable react-hooks/rules-of-hooks */
import './Dashboard.css';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import {
    addUserRole,
    deleteUserRole,
    loadUserRoles,
    mapUserRole,
    updateUserRole,
    UserRoleItem
} from '../services/adminApi';
import {useMsal} from '@azure/msal-react';
import React from 'react';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    RowSelectionState,
    SortingState,
    useReactTable,
} from '@tanstack/react-table';
import {Button, Typography} from '@mui/material';
import UserRoleDetails from '../components/UserRoleDetails';
import dayjs from 'dayjs';
import HtmlTooltip from "../components/HtmlTooltip.tsx";

const AdminConsole: React.FC = () => {
    const { instance } = useMsal();
    const [selectedRow, setSelectedRow] = React.useState<RowSelectionState>();
    const [editItem, setEditItem] = React.useState<UserRoleItem>();
    const [originalItem, setOriginalItem] = React.useState<UserRoleItem>();
    const [isViewMode, setIsViewMode] = React.useState<boolean>(true);
    const [data, setData] = React.useState<UserRoleItem[]>([]);
    const [sorting, setSorting] = React.useState<SortingState>([]);
    const [filter, setFilter] = React.useState('');
    const [isLoading, setIsLoading] = React.useState(true);
    const [isFormOpen, setIsFormOpen] = React.useState(false);
    const handleOpen = (pseudoId: string) => {
        setIsViewMode(true);
        const selectedItem = data.find(x => x.pseudoId == pseudoId);
        setOriginalItem(JSON.parse(JSON.stringify(selectedItem)));
        setEditItem(selectedItem);
        setIsFormOpen(true);
    };
    const handleSave = async () => {
        if (!!editItem.pseudoId) {
            const updatedEntry = await updateUserRole(instance, editItem);
            await mapUserRole(updatedEntry, editItem);
        }
        else {
            const newEntry = await addUserRole(instance, editItem);
            await mapUserRole(newEntry, editItem);
            data.unshift(editItem);
        }
        setData(data.map(item => item));
        setIsFormOpen(false);
        if (editItem.isTerritoryRelated === true || editItem.wasTerritoryRelated === true) {
            await loadData();
        }
    };

    const handleDelete = async () => {
        setIsFormOpen(false);
        await deleteUserRole(instance, editItem);
        await loadData();
    }

    const addNewUserRole = async () => {
        const newItem = {
            id: null,
            pseudoId: null,
            isTerritoryRelated: null,
            wasTerritoryRelated: null,
            territory: null,
            role: null,
            userEmail: null,
            lastUpdateTime: null,
            userName: null
        };
        setIsViewMode(false);
        setEditItem(newItem);
        setIsFormOpen(true);
    };

    const handleClose = async () => {
        await mapUserRole(originalItem, editItem);
        setIsFormOpen(false);
    }

    const columns: ColumnDef<UserRoleItem>[] = [
        {
            accessorFn: (a) => a.userName + "#$#" + a.userEmail,
            header: 'User',
            filterFn: 'includesString',
            cell: ({ row }) => {
                return (
                    <HtmlTooltip placement="top" title={<React.Fragment><Typography color="inherit">{(row.original.userName ?? "") + " (" + row.original.userEmail + ")"}</Typography></React.Fragment>} >
                        <div style={{ verticalAlign: 'middle', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}><div style={{ width: '200px', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{row.original.userName ?? row.original.userEmail}</div></div>
                    </HtmlTooltip>
                );
            },
        },
        {
            accessorKey: 'role',
            header: 'Role',
            filterFn: 'includesString',
            cell: ({ row }) => {
                return (
                    <HtmlTooltip placement="top" title={<React.Fragment><Typography color="inherit">{row.original.role}</Typography></React.Fragment>} >
                        <div style={{ width: '190px', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{row.original.role}</div>
                    </HtmlTooltip>
                );
            },
        },
        {
            accessorKey: 'territory',
            header: 'Territory',
            filterFn: 'includesString',
            cell: ({ row }) => {
                return (
                    <HtmlTooltip placement="top" title={<React.Fragment><Typography color="inherit">{row.original.territory}</Typography></React.Fragment>} >
                        <div style={{ width: '250px', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{row.original.territory}</div>
                    </HtmlTooltip>
                );
            },
        },
        {
            accessorKey: 'lastUpdateTime',
            header: 'LastUpdateTime',
            filterFn: 'includesString',
            cell: ({ row }) => {
                return (
                    <HtmlTooltip placement="top" title={<React.Fragment><Typography color="inherit">{row.original.lastUpdateTime == null ? null : dayjs(row.original.lastUpdateTime).format('YYYY-MM-DD HH:mm')}</Typography></React.Fragment>} >
                        <div style={{ width: '220px', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{row.original.lastUpdateTime == null ? null : dayjs(row.original.lastUpdateTime).format('YYYY-MM-DD HH:mm')}</div>
                    </HtmlTooltip>
                );
            },
        }
    ];

    const table = useReactTable({
        data,
        columns,
        state: {
            sorting,
            globalFilter: filter,
        },
        onSortingChange: setSorting,
        onGlobalFilterChange: setFilter,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getColumnCanGlobalFilter: () => true,
        autoResetPageIndex: false,
        initialState: {
            pagination: {
                pageSize: 10,
            },
        },
        getRowId: row => row?.pseudoId?.toString(),
        onRowSelectionChange: setSelectedRow,
        enableRowSelection: true,
        enableMultiRowSelection: false,
        defaultColumn: {
            minSize: 0,
            size: 400,
            maxSize: 400,
        }
    });

    const loadData = async () => {
        setIsLoading(true);
        try {
            const list = await loadUserRoles(instance);
            setData(list);
        } finally {
            setIsLoading(false);
        }
    };

    React.useEffect(() => {
        loadData();
    }, []);
    React.useEffect(() => {
        if (selectedRow) {
            const selectedId = Object.keys(selectedRow)[0];
            handleOpen(selectedId);
        }
    }, [selectedRow]);

    return (
        <div className="dashboard-container">
            <div className="dashboard-header">
                <div className="settings-content" style={{ width: '100%' }}>
                    <h1>Admin Console</h1>
                    <p style={{ textAlign: 'left', margin: '0px', marginTop: '50px', fontSize: '18px', maxWidth: '1400px' }}>
                        User management
                    </p>
                </div>
                <div className="settings-content" style={{ width: '100%' }}>
                    <div className="app-list-title">
                        User roles list
                    </div>
                    <div className="settings-table-container">
                        <div className="table-controls">
                            <div className="table-controls">
                                <Button variant="contained" color="inherit" onClick={addNewUserRole} startIcon={<AddCircleOutlineIcon color="success" />} style={{ marginRight: '20px' }}>Add new</Button>
                                <input
                                    type="text"
                                    placeholder="Filter..."
                                    value={filter}
                                    onChange={(e) => setFilter(e.target.value)}
                                    className="filter-input"
                                />
                            </div>
                        </div>
                        <div className="table-apps table-wrapper">
                            <table>
                                <thead>
                                    {table.getHeaderGroups().map(headerGroup => (
                                        <tr key={headerGroup.id}>
                                            {headerGroup.headers.map(header => (
                                                <th
                                                    key={header.id}
                                                    style={{ width: header.getSize(), fontWeight: 'bold' }}
                                                    onClick={header.column.getToggleSortingHandler()}
                                                >
                                                    {flexRender(
                                                        header.column.columnDef.header,
                                                        header.getContext()
                                                    )}
                                                    {{
                                                        asc: ' 🔼',
                                                        desc: ' 🔽',
                                                    }[header.column.getIsSorted() as string] ?? null}
                                                </th>
                                            ))}
                                        </tr>
                                    ))}
                                </thead>
                                <tbody>
                                    {isLoading ? (
                                        <tr>
                                            <td colSpan={4} className="loading-cell">Loading...</td>
                                        </tr>
                                    ) : (
                                        table.getRowModel().rows.map(row => (
                                            <tr key={row.id} style={{ cursor: 'pointer' }} onClick={row.getToggleSelectedHandler()}>
                                                {row.getVisibleCells().map(cell => (
                                                    <td key={cell.id}>
                                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                        <div className="pagination-controls">
                            <button
                                onClick={() => table.setPageIndex(0)}
                                disabled={!table.getCanPreviousPage()}
                            >
                                {'<<'}
                            </button>
                            <button
                                onClick={() => table.previousPage()}
                                disabled={!table.getCanPreviousPage()}
                            >
                                {'<'}
                            </button>
                            <span>
                                Page{' '}
                                <strong>
                                    {table.getState().pagination.pageIndex + 1} of{' '}
                                    {table.getPageCount()}
                                </strong>
                            </span>
                            <button
                                onClick={() => table.nextPage()}
                                disabled={!table.getCanNextPage()}
                            >
                                {'>'}
                            </button>
                            <button
                                onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                                disabled={!table.getCanNextPage()}
                            >
                                {'>>'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <UserRoleDetails handleClose={handleClose} handleSave={handleSave} isModalOpen={isFormOpen} isViewMode={isViewMode} model={editItem} setIsViewMode={setIsViewMode} handleDelete={handleDelete} />
        </div>
    );
};

export default AdminConsole; 